import React from 'react';
import { Box, Checkbox, Stack, Typography } from '@mui/material';
import {
  DraftPolicy,
  DraftPolicySection,
  DraftPolicySubSection,
  DraftManual,
} from 'views/App/Portal/Policy/_types/manuals/draft';
import draftManualHelper from '../../../helpers/draftManualHelper';
import { neutral } from '_lex/_foundations/phxColors';

interface PolicyContentTreeProps {
  policyData: DraftPolicy;
  manual: DraftManual;
  selectedContentIds: string[];
  policyCheckboxState: 'checked' | 'unchecked' | 'indeterminate';
  onPolicyToggle: () => void;
  onSectionToggle: (sectionId: string) => void;
  onSubsectionToggle: (subsectionId: string, sectionId: string) => void;
  areAllSubsectionsSelected: (sectionId: string) => boolean;
  areSomeSubsectionsSelected: (sectionId: string) => boolean;
}

const PolicyContentTree: React.FC<PolicyContentTreeProps> = ({
  policyData,
  manual,
  selectedContentIds,
  policyCheckboxState,
  onPolicyToggle,
  onSectionToggle,
  onSubsectionToggle,
  areAllSubsectionsSelected,
  areSomeSubsectionsSelected,
}) => {
  const policyNumber = draftManualHelper.getNumberManualNode(policyData, manual);

  const getSectionCheckboxState = (section: DraftPolicySection): 'checked' | 'unchecked' | 'indeterminate' => {
    const hasSubsections = (section.subSections?.length || 0) > 0;

    if (!hasSubsections) {
      // Section with no subsections - checked if section is selected
      return selectedContentIds.includes(section.id) ? 'checked' : 'unchecked';
    }

    if (areAllSubsectionsSelected(section.id) && selectedContentIds.includes(section.id)) {
      return 'checked';
    }
    if (
      areSomeSubsectionsSelected(section.id) ||
      (selectedContentIds.includes(section.id) && !areAllSubsectionsSelected(section.id))
    ) {
      return 'indeterminate';
    }
    return 'unchecked';
  };

  return (
    <Box
      sx={{
        border: `1px solid ${neutral[200]}`,
        borderRadius: 2,
        p: 2,
        backgroundColor: 'background.paper',
      }}
    >
      <Stack spacing={1}>
        {/* Policy level checkbox */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 1,
            px: 2,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          <Checkbox
            checked={policyCheckboxState === 'checked'}
            indeterminate={policyCheckboxState === 'indeterminate'}
            onChange={onPolicyToggle}
            sx={{ mr: 1 }}
          />
          <Typography
            variant='body1'
            sx={{ fontWeight: 600 }}
          >
            {policyNumber} {policyData.title}
          </Typography>
        </Box>

        {/* Sections */}
        {policyData.sections && policyData.sections.length > 0 ? (
          policyData.sections.map((section) => {
            const sectionNumber = draftManualHelper.getNumberPolicyNode(section, policyData);
            const sectionCheckboxState = getSectionCheckboxState(section);
            const isSectionDisabled = section.state === 'DISABLED';

            return (
              <Box
                key={section.id}
                sx={{
                  ml: 3,
                }}
              >
                {/* Section checkbox */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    py: 1,
                    px: 2,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <Checkbox
                    checked={sectionCheckboxState === 'checked'}
                    indeterminate={sectionCheckboxState === 'indeterminate'}
                    onChange={() => onSectionToggle(section.id)}
                    sx={{ mr: 1 }}
                  />
                  <Typography
                    variant='body2'
                    sx={{
                      fontWeight: 500,
                      color: isSectionDisabled ? neutral[400] : 'text.primary',
                      textDecoration: isSectionDisabled ? 'line-through' : 'none',
                    }}
                  >
                    {sectionNumber ? `${policyNumber}.${sectionNumber}` : policyNumber} {section.title}
                  </Typography>
                </Box>

                {/* Subsections */}
                {section.subSections?.map((subsection) => {
                  const subsectionNumber = draftManualHelper.getNumberPolicyNode(subsection, policyData);
                  const isSubsectionDisabled = subsection.state === 'DISABLED';
                  const isSubsectionSelected = selectedContentIds.includes(subsection.id);

                  return (
                    <Box
                      key={subsection.id}
                      sx={{
                        ml: 3,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 1,
                          px: 2,
                          borderRadius: 1,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                        }}
                      >
                        <Checkbox
                          checked={isSubsectionSelected}
                          onChange={() => onSubsectionToggle(subsection.id, section.id)}
                          sx={{ mr: 1 }}
                        />
                        <Typography
                          variant='body2'
                          sx={{
                            color: isSubsectionDisabled ? neutral[400] : 'text.primary',
                            textDecoration: isSubsectionDisabled ? 'line-through' : 'none',
                          }}
                        >
                          {subsectionNumber ? `${policyNumber}.${subsectionNumber}` : policyNumber} {subsection.title}
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            );
          })
        ) : (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography
              variant='body2'
              sx={{ color: 'text.secondary' }}
            >
              No sections available
            </Typography>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default PolicyContentTree;
